import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Site } from './types/Site';
import { parseKMLFile } from './utils/kmlParser';
import SiteMap from './components/SiteMap';
import Auth from './components/Auth';
import AccountSettings from './components/AccountSettings';
import AdminPanel from './components/AdminPanel';
import SupabaseDiagnostic from './components/SupabaseDiagnostic';
import ResetPassword from './components/ResetPassword';
import RegionSelection from './components/RegionSelection';
import LandingPage from './components/LandingPage';
import WelcomePage from './components/WelcomePage';
import ResourcesPage from './components/ResourcesPage';
import SignalBoxDirectory from './components/SignalBoxDirectory';
import HiPathAllocations from './components/HiPathAllocations';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { saveSitesToJson, fetchBatterySites, fetchSdhSites, fetchLocSites, fetchPdhSites, fetchTelecomsSites, fetchSptSites, fetchFibreSites, fetchSitesFromJson } from './utils/siteUtils';
import Message from './components/Message';

// Offline/demo mode when Supabase is not configured or explicitly enabled
const isOfflineMode = !process.env.REACT_APP_SUPABASE_URL?.trim() || process.env.REACT_APP_OFFLINE_MODE === 'true';

// Helper function to deduplicate sites by coordinates
const deduplicateSitesByCoordinates = (sites: Site[]): Site[] => {
  // Use a map to keep track of sites by their coordinates
  const uniqueSites = new Map<string, Site>();
  
  sites.forEach(site => {
    const key = `${site.coordinates.lat},${site.coordinates.lng}`;
    
    // If this is the first site at this location, add it
    if (!uniqueSites.has(key)) {
      uniqueSites.set(key, site);
    }
  });
  
  // Convert map values back to array
  return Array.from(uniqueSites.values());
};

const AppContent = (): React.ReactElement => {
  // State variables
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const { user, signOut, getUserStatus } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [userStatusLoading, setUserStatusLoading] = useState(true);
  const userStatusLoaded = useRef(false);
  const [showWelcomePage, setShowWelcomePage] = useState(true);
  const [showResourcesPage, setShowResourcesPage] = useState(false);
  const [showResourceDetail, setShowResourceDetail] = useState<string | null>(null);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showAssetSelection, setShowAssetSelection] = useState(true);
  const [selectedAssetType, setSelectedAssetType] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  
  // Use useMemo to deduplicate sites for display on the map
  const uniqueSites = useMemo(() => {
    return deduplicateSitesByCoordinates(sites);
  }, [sites]);
  
  // Check for special pages
  const pathname = window.location.pathname;
  const showDiagnosticPage = pathname === '/diagnostic';
  const showResetPasswordPage = pathname === '/reset-password' || 
                               (window.location.hash && window.location.hash.includes('type=recovery'));
  
  // Load user profile data including admin and approval status
  useEffect(() => {
    if (!user) return;
    
    const loadUserStatus = async () => {
      try {
        // Only show loading spinner on the very first status check
        if (!userStatusLoaded.current) {
          setUserStatusLoading(true);
        }
        console.log('Fetching user status for:', user.email);
        const status = await getUserStatus();
        console.log('User status received:', status);
        
        // Force admin status for specific email
        if (user.email === 'jamesperrin1999@gmail.com') {
          console.log('Special admin user detected, setting admin status to true');
          setIsAdmin(true);
          setIsApproved(true);
        } else {
          setIsAdmin(status.isAdmin);
          setIsApproved(status.isApproved);
        }
      } catch (err) {
        console.error('Error in user status fetch:', err);
        // Force admin status for specific email even in case of error
        if (user.email === 'jamesperrin1999@gmail.com') {
          console.log('Special admin user detected (error case), forcing admin status');
          setIsAdmin(true);
          setIsApproved(true);
        }
      } finally {
        setUserStatusLoading(false);
        userStatusLoaded.current = true;
      }
    };
    
    loadUserStatus();
  }, [user, getUserStatus]);

  // Simplified logging - only log that we're authenticated
  useEffect(() => {
    if (user) {
      console.log('User authenticated:', !!user);
    }
  }, [user]);

  // Load site data based on selected asset type
  useEffect(() => {
    const loadSitesData = async () => {
      try {
        if (!selectedAssetType) {
          return;
        }
        setLoading(true);
        
        let sitesData: Site[] = [];
        
        // Handle different asset types
        if (selectedAssetType === 'battery_sites') {
          // Load battery sites specifically
          sitesData = await fetchBatterySites();
          console.log(`Loaded ${sitesData.length} battery sites`);
        } 
        else if (selectedAssetType === 'sdh_sites') {
          console.log('Loading SDH sites specifically');
          try {
            sitesData = await fetchSdhSites();
            // Ensure siteType is set correctly
            sitesData = sitesData.map(site => ({
              ...site,
              name: {
                ...site.name,
                siteType: 'SDH'
              }
            }));
            console.log(`Loaded ${sitesData.length} SDH sites from sdh_sites table`);
          } catch (err) {
            console.error('Error loading SDH sites:', err);
            setError('Failed to load SDH sites. Please try again.');
            sitesData = [];
          }
        }
        else if (selectedAssetType === 'pdh_sites') {
          console.log('Loading PDH sites specifically');
          try {
            sitesData = await fetchPdhSites();
            // Ensure siteType is set correctly
            sitesData = sitesData.map(site => ({
              ...site,
              name: {
                ...site.name,
                siteType: 'PDH'
              }
            }));
            console.log(`Loaded ${sitesData.length} PDH sites from pdh_sites table`);
          } catch (err) {
            console.error('Error loading PDH sites:', err);
            setError('Failed to load PDH sites. Please try again.');
            sitesData = [];
          }
        }
        else if (selectedAssetType === 'loc_sites') {
          // Load LOC sites specifically
          sitesData = await fetchLocSites();
          console.log(`Loaded ${sitesData.length} LOC sites`);
        }
        else if (selectedAssetType === 'telecoms_sites') {
          // Load telecoms sites specifically
          sitesData = await fetchTelecomsSites();
          console.log(`Loaded ${sitesData.length} telecoms sites`);
        }
        else if (selectedAssetType === 'spt_sites') {
          console.log('Loading SPT sites');
          try {
            sitesData = await fetchSptSites();
            console.log(`Loaded ${sitesData.length} SPT sites from spt_sites table`);
          } catch (err) {
            console.error('Error loading SPT sites:', err);
            setError('Failed to load SPT sites. Please try again.');
            sitesData = [];
          }
        }
        else if (selectedAssetType === 'fibre_sites') {
          console.log('Loading Fibre Cable sites');
          try {
            sitesData = await fetchFibreSites();
            console.log(`Loaded ${sitesData.length} Fibre sites from fibre_sites table`);
          } catch (err) {
            console.error('Error loading Fibre sites:', err);
            setError('Failed to load Fibre Cable sites. Please try again.');
            sitesData = [];
          }
        }
        
        setSites(sitesData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading site data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load site data');
        setLoading(false);
      }
    };
    
    // Only load sites if the user is approved and an asset type is selected
    if (user && isApproved && !userStatusLoading && selectedAssetType) {
      loadSitesData();
    } else if (user && !userStatusLoading && !selectedAssetType) {
      setLoading(false);
    } else if (!user) {
      setLoading(false);
    }
    
    // Add listener for site updates
    const handleSiteUpdated = () => {
      console.log('Site updated event received, reloading site data');
      if (user && isApproved && !userStatusLoading && selectedAssetType) {
        loadSitesData();
      }
    };
    
    window.addEventListener('site-updated', handleSiteUpdated);
    
    return () => {
      window.removeEventListener('site-updated', handleSiteUpdated);
    };
  }, [user, isApproved, userStatusLoading, selectedAssetType]);

  const handleAccountSettingsOpen = () => {
    setShowAccountSettings(true);
    setShowAdminPanel(false);
  };

  const handleAccountSettingsClose = () => {
    setShowAccountSettings(false);
  };

  const handleAdminPanelOpen = () => {
    setShowAdminPanel(true);
    setShowAccountSettings(false);
  };

  const handleAdminPanelClose = () => {
    setShowAdminPanel(false);
  };

  const handleUpdateSites = async (newSites: Site[], skipJsonDownload = false) => {
    setSites(newSites);
    
    // Save changes to JSON file if an asset type is selected and skipJsonDownload is false
    if (selectedAssetType && !skipJsonDownload) {
      try {
        await saveSitesToJson(selectedAssetType, newSites);
        // Show success message to user
        setMessage({ text: 'Changes saved successfully', type: 'success' });
      } catch (error) {
        console.error('Failed to save changes:', error);
        setMessage({ 
          text: 'Failed to save changes. Please try again.', 
          type: 'error' 
        });
      }
    }
  };

  const handleWelcomeMapsSelect = () => {
    setShowWelcomePage(false);
    // showLandingPage is already true, so the asset picker will render next
  };

  const handleWelcomeResourcesSelect = () => {
    setShowWelcomePage(false);
    setShowResourcesPage(true);
  };

  const handleResourcesBack = () => {
    setShowResourcesPage(false);
    setShowWelcomePage(true);
  };

  const handleSignalBoxesSelect = () => {
    setShowResourcesPage(false);
    setShowResourceDetail('signal_boxes');
  };

  const handleHiPathSelect = () => {
    setShowResourcesPage(false);
    setShowResourceDetail('hipath');
  };

  const handleResourceDetailBack = () => {
    setShowResourceDetail(null);
    setShowResourcesPage(true);
  };

  const handleAssetTypeSelect = (assetTypeId: string) => {
    setSelectedAssetType(assetTypeId);
    setShowAssetSelection(false);
    setShowLandingPage(false);
    setShowWelcomePage(false);
  };

  const handleLandingBrowseAll = () => {
    // Go back to the Welcome Page
    setShowWelcomePage(true);
  };
  
  const handleChangeAssetType = () => {
    // Reset back to the welcome page
    setShowWelcomePage(true);
    setShowResourcesPage(false);
    setShowResourceDetail(null);
    setShowLandingPage(true);
    setShowAssetSelection(true);
    setSelectedAssetType(null);
    setSites([]);
  };
  
  // Render special pages
  if (showDiagnosticPage) {
    return <SupabaseDiagnostic />;
  }
  
  if (showResetPasswordPage) {
    return <ResetPassword />;
  }

  if (!user) {
    return <Auth />;
  }

  // Show approval waiting message if user is not approved
  if (user && !isApproved && !userStatusLoading) {
    console.log('Showing approval waiting screen for user:', user.email);
    console.log('isApproved status:', isApproved);
    console.log('userStatusLoading:', userStatusLoading);
    
    // Check if this is an existing user (jamesperrin1999@gmail.com)
    if (user.email === 'jamesperrin1999@gmail.com') {
      console.log('Special user detected, bypassing approval check');
      // Force approve this specific account
      setTimeout(() => setIsApproved(true), 100);
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: 'white',
          backgroundColor: '#1a1a1a'
        }}>
          Loading...
        </div>
      );
    }
    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        backgroundColor: '#1a1a1a',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>Account Pending Approval</h2>
        <p>Your account is waiting for administrator approval.</p>
        <p>You will be able to access the application once your account is approved.</p>
        <button 
          onClick={signOut}
          style={{
            marginTop: '20px',
            padding: '8px 16px',
            background: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  // Show welcome page (top-level app hub)
  if (user && isApproved && showWelcomePage && !selectedAssetType) {
    return (
      <>
        <WelcomePage
          onMapsSelect={handleWelcomeMapsSelect}
          onResourcesSelect={handleWelcomeResourcesSelect}
          onAdminPanel={handleAdminPanelOpen}
          onSignOut={signOut}
          userEmail={user.email || ''}
          isAdmin={isAdmin}
        />
        {showAdminPanel && (
          <AdminPanel
            onClose={handleAdminPanelClose}
            sites={sites}
            onUpdateSites={handleUpdateSites}
          />
        )}
      </>
    );
  }

  // Show resources page
  if (user && isApproved && showResourcesPage && !showResourceDetail) {
    return (
      <ResourcesPage
        onBack={handleResourcesBack}
        onSignalBoxes={handleSignalBoxesSelect}
        onHiPathAllocations={handleHiPathSelect}
      />
    );
  }

  // Show resource detail page (e.g. signal box directory)
  if (user && isApproved && showResourceDetail === 'signal_boxes') {
    return (
      <SignalBoxDirectory onBack={handleResourceDetailBack} />
    );
  }

  if (user && isApproved && showResourceDetail === 'hipath') {
    return (
      <HiPathAllocations onBack={handleResourceDetailBack} />
    );
  }

  // Show landing page (asset type picker) if the user chose Maps
  if (user && isApproved && showLandingPage && !selectedAssetType) {
    return (
      <LandingPage
        onAssetSelect={handleAssetTypeSelect}
        onBrowseAll={handleLandingBrowseAll}
        onSignOut={signOut}
        userEmail={user.email || ''}
      />
    );
  }

  // Show asset type selection page if the user is logged in and approved
  if (user && isApproved && showAssetSelection && !selectedAssetType) {
    return <RegionSelection onRegionSelect={handleAssetTypeSelect} />;
  }

  if (loading || userStatusLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        backgroundColor: '#1a1a1a'
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'red',
        backgroundColor: '#1a1a1a',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>Error Loading Sites</h2>
        <p>{error}</p>
        <p style={{ fontSize: '14px', marginTop: '10px' }}>
          Please check the browser console for more details.
        </p>
      </div>
    );
  }

  return (
    <div className="App">
      {message && (
        <Message 
          text={message.text} 
          type={message.type} 
          onClose={() => setMessage(null)} 
        />
      )}
      <div style={{ position: 'relative', height: '100vh' }}>
        <SiteMap 
          sites={uniqueSites} 
          onLogout={signOut} 
          userEmail={user?.email || ''}
          onAccountSettings={handleAccountSettingsOpen}
          onAdminPanel={handleAdminPanelOpen}
          onChangeAssetType={handleChangeAssetType}
          isAdmin={isAdmin}
          currentAssetType={selectedAssetType || ''}
        />
        {showAccountSettings && (
          <AccountSettings 
            userEmail={user.email || ''} 
            onClose={handleAccountSettingsClose}
            onLogout={signOut}
          />
        )}
        {showAdminPanel && (
          <AdminPanel
            onClose={handleAdminPanelClose}
            sites={sites}
            onUpdateSites={handleUpdateSites}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Offline/demo app: no Supabase. Loads sites from public/data/regions/*.json.
 * Use when REACT_APP_SUPABASE_URL is missing or REACT_APP_OFFLINE_MODE=true.
 */
const OfflineApp = (): React.ReactElement => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssetSelection, setShowAssetSelection] = useState(true);
  const [selectedAssetType, setSelectedAssetType] = useState<string | null>(null);

  const uniqueSites = useMemo(() => deduplicateSitesByCoordinates(sites), [sites]);

  useEffect(() => {
    if (!selectedAssetType) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSitesFromJson();
        setSites(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sites');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedAssetType]);

  const handleAssetTypeSelect = (assetTypeId: string) => {
    setSelectedAssetType(assetTypeId);
    setShowAssetSelection(false);
  };

  const handleChangeAssetType = () => {
    setShowAssetSelection(true);
    setSelectedAssetType(null);
    setSites([]);
  };

  if (showAssetSelection || !selectedAssetType) {
    return <RegionSelection onRegionSelect={handleAssetTypeSelect} />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#fff', backgroundColor: '#1a1a1a' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#f87171', backgroundColor: '#1a1a1a', padding: 20, textAlign: 'center' }}>
        <h2>Error loading sites</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="App">
      <div style={{ position: 'relative', height: '100vh' }}>
        <SiteMap
          sites={uniqueSites}
          onLogout={handleChangeAssetType}
          userEmail="demo@offline"
          onAccountSettings={() => {}}
          onAdminPanel={() => {}}
          onChangeAssetType={handleChangeAssetType}
          isAdmin={false}
          currentAssetType={selectedAssetType || ''}
        />
      </div>
    </div>
  );
};

const App = (): React.ReactElement => {
  if (isOfflineMode) {
    return <OfflineApp />;
  }
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App; 